/**
 * SectionHeader Component
 * 
 * Reusable section header with consistent styling across the site.
 * 
 * DESIGN STANDARDS:
 * - Eyebrow: Small accent text, uppercase, tracking-wider
 * - Title: H2 with Newsreader font, responsive sizing (text-3xl md:text-5xl)
 * - Description: Optional subtitle in muted color
 * - Spacing: Consistent mb-16 bottom margin
 * 
 * @component
 * @example
 * ```tsx
 * <SectionHeader
 *   eyebrow="Our Services"
 *   title="Complete Logistics Solutions"
 *   description="From quay to final delivery, we provide end-to-end transport..."
 *   maxWidth="2xl"
 * />
 * ```
 */

import { memo } from "react";

interface SectionHeaderProps {
  /** Small uppercase label above title */
  eyebrow: string;
  /** Main section heading */
  title: string;
  /** Optional description text below title */
  description?: string;
  /** Max width constraint for description text */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  /** Additional CSS classes */
  className?: string;
}

export const SectionHeader = memo(({
  eyebrow,
  title,
  description,
  maxWidth = '2xl',
  className = '',
}: SectionHeaderProps) => (
  <div className={`text-center mb-16 ${className}`}>
    <p className="text-accent font-semibold text-sm tracking-wider uppercase mb-2">
      {eyebrow}
    </p>
    <h2 className="text-3xl md:text-5xl font-medium text-foreground mb-4 font-newsreader">
      {title}
    </h2>
    {description && (
      <p className={`text-lg text-muted-foreground max-w-${maxWidth} mx-auto`}>
        {description}
      </p>
    )}
  </div>
));

SectionHeader.displayName = "SectionHeader";
